import React, {
  useEffect,
  useRef,
  useState,
  useSyncExternalStore
} from 'react';
import { TableV3FilterItem } from 'common/table/types/TableV3';
import { CandidateFilterMatchModeToggle } from 'modules/candidate-profile/components/CandidateFilterMatchModeToggle';
import { useTableV3FilterSelection } from 'common/table/components/TableV3FilterSelectionContext';
import { CandidateFilterMatchMode } from 'modules/candidate-profile/types/CandidateSearchFilter';

type CandidateFilterModeHeaderProps = {
  field: string;
  getMode: (field: string) => CandidateFilterMatchMode;
  subscribeToModes: (listener: () => void) => () => void;
  getValuesForMode: (field: string, mode: CandidateFilterMatchMode) => string[];
  onChange: (
    field: string,
    mode: CandidateFilterMatchMode,
    options?: { push?: boolean }
  ) => void;
  onSetModeValues?: (
    field: string,
    mode: CandidateFilterMatchMode,
    items: TableV3FilterItem[]
  ) => void;
};

type PickedByMode = Partial<
  Record<CandidateFilterMatchMode, TableV3FilterItem[]>
>;

/**
 * Reads the mode through a subscription rather than a prop, so the column
 * definition carrying this header stays referentially stable; rebuilding
 * columns closes the open dropdown.
 */
export const CandidateFilterModeHeader: React.FC<
  CandidateFilterModeHeaderProps
> = ({
  field,
  getMode,
  subscribeToModes,
  getValuesForMode,
  onChange,
  onSetModeValues
}) => {
  const mode = useSyncExternalStore(subscribeToModes, () => getMode(field));
  const selection = useTableV3FilterSelection();
  const deferral = selection?.deferral;
  const [pendingMode, setPendingMode] = useState<CandidateFilterMatchMode>();

  // Nothing commits until Apply, so picks left on a mode live here.
  const pickedRef = useRef<PickedByMode>({});

  useEffect(
    () =>
      deferral?.subscribeToSettled(() => {
        pickedRef.current = {};
        setPendingMode(undefined);
      }),
    [deferral]
  );

  const shownMode = pendingMode ?? mode;

  const applyImmediately = (next: CandidateFilterMatchMode) => {
    onChange(field, next);
    selection?.setSelection(getValuesForMode(field, next));
  };

  const holdUntilApply = (next: CandidateFilterMatchMode) => {
    if (!selection || !deferral) return;

    pickedRef.current[shownMode] = selection.getSelectedItems();
    setPendingMode(next);
    selection.setSelection(
      pickedRef.current[next]?.map((item) => item.value) ??
        getValuesForMode(field, next)
    );

    deferral.defer(() => {
      Object.entries(pickedRef.current)
        .filter(([picked]) => picked !== next)
        .forEach(([picked, items]) =>
          onSetModeValues?.(field, picked as CandidateFilterMatchMode, items)
        );

      onChange(field, next, { push: false });
    });
  };

  return (
    <CandidateFilterMatchModeToggle
      value={shownMode}
      onChange={deferral ? holdUntilApply : applyImmediately}
    />
  );
};
